import {
  CategoryRepository,
  CreateCategoryDTO,
  UpdateCategoryDTO,
  CreateSubCategoryDTO,
  UpdateSubCategoryDTO,
} from '../../domain/repositories/category.repository';
import { CategoryEntity, SubcategoryProps } from '../../domain/entities/category.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { resolveMediaUrl } from '../utils/media-url.helper';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface RawSubCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageId?: string | null;
  imageUrl?: string | null;
  status?: boolean | string | number;
  displayOrder?: number;
  categoryId?: string;
  categoryName?: string;
  categorySlug?: string;
  productsCount?: number;
}

interface RawCategory {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  imageId?: string | null;
  imageUrl?: string | null;
  status?: boolean | string | number;
  showOnHome?: boolean;
  displayOrder?: number;
  productsCount?: number;
  subCategories?: RawSubCategory[];
}

export class HttpCategoryRepository implements CategoryRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  private normalizeStatus(status: unknown): 'active' | 'inactive' {
    if (status === false || status === 'false' || status === 'inactive' || status === 'INACTIVE' || status === 0) {
      return 'inactive';
    }
    return 'active';
  }

  private mapRawCategory(c: RawCategory): CategoryEntity {
    const subcategories: SubcategoryProps[] = (c.subCategories || []).map((sub) => ({
      id: sub.id,
      name: sub.name,
      slug: sub.slug,
      description: sub.description,
      imageId: sub.imageId,
      imageUrl: sub.imageUrl ? resolveMediaUrl(sub.imageUrl) : null,
      status: this.normalizeStatus(sub.status),
      displayOrder: sub.displayOrder,
      categoryId: sub.categoryId,
      categoryName: sub.categoryName,
      categorySlug: sub.categorySlug,
      itemCount: sub.productsCount ?? 0,
    }));

    return new CategoryEntity({
      id: c.id,
      name: c.name,
      slug: c.slug,
      description: c.description ?? '',
      itemCount: c.productsCount ?? 0,
      subcategories,
      imageId: c.imageId ?? null,
      imageUrl: c.imageUrl ? resolveMediaUrl(c.imageUrl) : null,
      status: this.normalizeStatus(c.status),
      showOnHome: !!c.showOnHome,
      displayOrder: c.displayOrder ?? 0,
    });
  }

  private mapRawSubCategory(s: RawSubCategory): SubcategoryProps {
    return {
      id: s.id,
      name: s.name,
      slug: s.slug,
      description: s.description,
      imageId: s.imageId,
      imageUrl: s.imageUrl ? resolveMediaUrl(s.imageUrl) : null,
      status: this.normalizeStatus(s.status),
      displayOrder: s.displayOrder,
      categoryId: s.categoryId,
      categoryName: s.categoryName,
      categorySlug: s.categorySlug,
      itemCount: s.productsCount ?? 0,
    };
  }

  public async getCategories(optionsOrActiveOnly?: boolean | {
    activeOnly?: boolean;
    search?: string;
    includeSubcategories?: boolean;
    showOnHomeOnly?: boolean;
  }): Promise<CategoryEntity[]> {
    let query = '?all=true';
    if (typeof optionsOrActiveOnly === 'boolean') {
      query = optionsOrActiveOnly ? '?activeOnly=true' : '?all=true';
    } else if (optionsOrActiveOnly) {
      const params = new URLSearchParams();
      if (optionsOrActiveOnly.activeOnly) params.append('activeOnly', 'true');
      if (optionsOrActiveOnly.showOnHomeOnly) params.append('showOnHomeOnly', 'true');
      if (optionsOrActiveOnly.search) params.append('search', optionsOrActiveOnly.search);
      if (optionsOrActiveOnly.includeSubcategories !== undefined) {
        params.append('includeSubcategories', String(optionsOrActiveOnly.includeSubcategories));
      }
      query = params.toString() ? `?${params.toString()}` : '';
    }
    const res = await this.httpClient.get<ApiResponse<RawCategory[]>>(`/categories${query}`);
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map((c) => this.mapRawCategory(c));
  }

  public async getCategoryById(id: string): Promise<CategoryEntity | null> {
    const res = await this.httpClient.get<ApiResponse<RawCategory>>(`/categories/${encodeURIComponent(id)}`);
    if (!res.success || !res.data) {
      return null;
    }
    return this.mapRawCategory(res.data);
  }

  public async createCategory(data: CreateCategoryDTO): Promise<CategoryEntity> {
    const payload: Record<string, any> = {
      name: data.name.trim(),
      slug: data.slug?.trim() || undefined,
      description: data.description?.trim() || null,
      imageId: data.imageId?.trim() ? data.imageId.trim() : null,
      imageUrl: data.imageUrl?.trim() ? data.imageUrl.trim() : null,
      displayOrder: Number(data.displayOrder) || 0,
      status: typeof data.status === 'boolean'
        ? data.status
        : data.status === 'active' || data.status === 'true',
      showOnHome: Boolean(data.showOnHome),
    };
    const res = await this.httpClient.post<ApiResponse<RawCategory>>('/categories', payload);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear la categoría');
    }
    return this.mapRawCategory(res.data);
  }

  public async updateCategory(id: string, data: UpdateCategoryDTO): Promise<CategoryEntity> {
    const payload: Record<string, any> = {
      name: data.name?.trim(),
      slug: data.slug?.trim() || undefined,
      description: data.description?.trim() || null,
      imageId: data.imageId?.trim() ? data.imageId.trim() : (data.imageId === null ? null : undefined),
      imageUrl: data.imageUrl?.trim() ? data.imageUrl.trim() : (data.imageUrl === null ? null : undefined),
      displayOrder: data.displayOrder !== undefined ? Number(data.displayOrder) : undefined,
      status: data.status !== undefined
        ? (typeof data.status === 'boolean' ? data.status : data.status === 'active' || data.status === 'true')
        : undefined,
      showOnHome: data.showOnHome !== undefined ? Boolean(data.showOnHome) : undefined,
    };
    const res = await this.httpClient.put<ApiResponse<RawCategory>>(
      `/categories/${encodeURIComponent(id)}`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la categoría');
    }
    return this.mapRawCategory(res.data);
  }

  public async changeCategoryStatus(id: string, status: string | boolean): Promise<CategoryEntity> {
    const boolStatus = typeof status === 'boolean' ? status : status === 'active' || status === 'true';
    const res = await this.httpClient.patch<ApiResponse<RawCategory>>(
      `/categories/${encodeURIComponent(id)}/status`,
      { status: boolStatus }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado de categoría');
    }
    return this.mapRawCategory(res.data);
  }

  public async toggleCategoryShowOnHome(id: string, showOnHome: boolean): Promise<CategoryEntity> {
    const res = await this.httpClient.patch<ApiResponse<RawCategory>>(
      `/categories/${encodeURIComponent(id)}/show-on-home`,
      { showOnHome }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar visibilidad en inicio');
    }
    return this.mapRawCategory(res.data);
  }

  public async deleteCategory(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(
      `/categories/${encodeURIComponent(id)}`
    );
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la categoría');
    }
  }

  // Subcategorías
  public async getSubCategories(params?: {
    categoryId?: string;
    activeOnly?: boolean;
    search?: string;
  }): Promise<SubcategoryProps[]> {
    const query = new URLSearchParams();
    if (params?.categoryId) query.append('categoryId', params.categoryId);
    if (params?.activeOnly) query.append('activeOnly', 'true');
    if (params?.search) query.append('search', params.search);

    const queryString = query.toString() ? `?${query.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<RawSubCategory[]>>(`/subcategories${queryString}`);
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map((s) => this.mapRawSubCategory(s));
  }

  public async createSubCategory(data: CreateSubCategoryDTO): Promise<SubcategoryProps> {
    const payload: Record<string, any> = {
      categoryId: data.categoryId.trim(),
      name: data.name.trim(),
      slug: data.slug?.trim() || undefined,
      description: data.description?.trim() || null,
      imageId: data.imageId?.trim() ? data.imageId.trim() : null,
      imageUrl: data.imageUrl?.trim() ? data.imageUrl.trim() : null,
      displayOrder: Number(data.displayOrder) || 0,
      status: typeof data.status === 'boolean'
        ? data.status
        : data.status === 'active' || data.status === 'true',
    };
    const res = await this.httpClient.post<ApiResponse<RawSubCategory>>('/subcategories', payload);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear la subcategoría');
    }
    return this.mapRawSubCategory(res.data);
  }

  public async updateSubCategory(id: string, data: UpdateSubCategoryDTO): Promise<SubcategoryProps> {
    const payload: Record<string, any> = {
      categoryId: data.categoryId?.trim() || undefined,
      name: data.name?.trim(),
      slug: data.slug?.trim() || undefined,
      description: data.description?.trim() || null,
      imageId: data.imageId?.trim() ? data.imageId.trim() : (data.imageId === null ? null : undefined),
      imageUrl: data.imageUrl?.trim() ? data.imageUrl.trim() : (data.imageUrl === null ? null : undefined),
      displayOrder: data.displayOrder !== undefined ? Number(data.displayOrder) : undefined,
      status: data.status !== undefined
        ? (typeof data.status === 'boolean' ? data.status : data.status === 'active' || data.status === 'true')
        : undefined,
    };
    const res = await this.httpClient.put<ApiResponse<RawSubCategory>>(
      `/subcategories/${encodeURIComponent(id)}`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la subcategoría');
    }
    return this.mapRawSubCategory(res.data);
  }

  public async changeSubCategoryStatus(id: string, status: string | boolean): Promise<SubcategoryProps> {
    const boolStatus = typeof status === 'boolean' ? status : status === 'active' || status === 'true';
    const res = await this.httpClient.patch<ApiResponse<RawSubCategory>>(
      `/subcategories/${encodeURIComponent(id)}/status`,
      { status: boolStatus }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al cambiar estado de la subcategoría');
    }
    return this.mapRawSubCategory(res.data);
  }

  public async deleteSubCategory(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(
      `/subcategories/${encodeURIComponent(id)}`
    );
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la subcategoría');
    }
  }
}
